import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import TestingSessionForm from '../../components/athletes/TestingSessionForm';
import { fetchTestTypes, getTestingSession } from '../../services/athleteService';

function TestingSessionFormInner() {
  const { athleteId, sessionId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(sessionId);

  const [testTypes, setTestTypes] = useState([]);
  const [existingSession, setExistingSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const types = await fetchTestTypes();
        setTestTypes(types);
        if (isEdit) {
          const session = await getTestingSession(sessionId);
          setExistingSession(session);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [sessionId, isEdit]);

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;

  // Test Result Correction/Invalidation (055) — the legacy full-session
  // edit form is Personal-only; update_testing_session_with_attempts()
  // now rejects a Team-sourced session server-side regardless, but this
  // message is a clearer client-side stop than letting someone fill out
  // the whole form only to hit that error on Save. Testing History
  // (DrillHistory.js) already never links here for a Team session — this
  // only guards someone reaching this route directly by URL.
  if (isEdit && existingSession?.team_id) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="info">
          Team testing results cannot be bulk-edited here; use the Invalidate/Correct actions in Testing History instead.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 2 }}>
        {isEdit ? 'Edit Testing Session' : 'Log Testing Session'}
      </Typography>
      <TestingSessionForm
        athleteId={athleteId}
        testTypes={testTypes}
        existingSession={existingSession}
        onSaved={() => navigate(`/athletes/${athleteId}`, { state: { tab: 1 } })}
        onCancel={() => navigate(`/athletes/${athleteId}`, { state: { tab: 1 } })}
      />
    </Container>
  );
}

export default function TestingSessionFormPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TestingSessionFormInner /></RequireAuth>
    </Box>
  );
}
